/**
 * CdvPurchase (cordova-plugin-purchase) integration for iOS IAP.
 *
 * Architecture (from shared-docs):
 *   JavaScript (CdvPurchase.store) -> cordova-plugin-purchase -> StoreKit (managed by plugin)
 *
 * MUST use cordova-plugin-purchase — NEVER a custom StoreKit Swift plugin.
 * Custom StoreKit plugins cause unrecoverable hangs (Task.detached + timeout races).
 */
import { Capacitor } from '@capacitor/core';

// CdvPurchase v13 user-cancel codes — canon: shared-docs/in-app-purchases.md
const CANCEL_CODES = new Set([6777006, 6777003, 6777010]);

const API_BASE = 'https://ctqysft3ek.execute-api.eu-west-2.amazonaws.com/tasks';

// Apple product ID mapping (App Store Connect subscription IDs)
export const APPLE_PRODUCT_IDS = {
  premium_monthly: 'uk.co.wolftechnologies.docket.premium.monthly',
  premium_yearly: 'uk.co.wolftechnologies.docket.premium.yearly',
};

// Reverse map (Apple product ID -> plan id). A product missing from here is
// the plugin's virtual `appstore.application` transaction or an unregistered
// SKU — never sent to the backend.
const APPLE_TO_INTERNAL = Object.fromEntries(
  Object.entries(APPLE_PRODUCT_IDS).map(([planId, appleId]) => [appleId, planId])
);

// Cross-user receipt contamination guard: the Apple receipt belongs to the
// device's Apple ID, not the app account, and `approved` re-fires for every
// owned receipt at store init. Only transactions approved within this window
// of a user-initiated purchase/restore reach the backend.
const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;
let userInitiatedAt = 0;

// Transactions the user just approved (purchase or restore), keyed
// transactionId -> productId; `verified` drains it. The object CdvPurchase
// hands to `verified` is a VerifiedReceipt with NO `.transaction` and NO
// `.productId` (and `sourceReceipt.transactions[0]` is the virtual
// `appstore.application` placeholder, not the sale), so `approved` is the only
// place the real transaction id is known. Fleet fix 2026-09-05 —
// shared-docs/in-app-purchases.md §"IAP Verification — Two-Stage Pipeline".
const pendingVerify = new Map();

let storeInstance = null;
let storeReady = false;
let readyCallbacks = [];

export function isIOS() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export function isStoreReady() {
  return storeReady;
}

/**
 * Silent, toast-free POST to /subscription/verify-apple. Never throws —
 * `verified` re-fires for re-delivered transactions on every launch, and a
 * stale receipt must not surface an error toast. Resolves true on 2xx.
 * Backend resolves originalTransactionId + plan via App Store Server API.
 */
async function postVerifyApple({ transactionId, productId }) {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      console.warn('[IAP] No idToken available, skipping verify-apple');
      return false;
    }
    const res = await fetch(`${API_BASE}/subscription/verify-apple`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
      body: JSON.stringify({ transactionId, productId }),
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      console.warn('[IAP] verify-apple failed:', res.status, errBody);
      return false;
    }
    console.log('[IAP] verify-apple succeeded:', { transactionId, productId });
    return true;
  } catch (err) {
    console.warn('[IAP] verify-apple error:', err);
    return false;
  }
}

/**
 * Initialize the CdvPurchase store. Call once on app mount (iOS only).
 * @param {Function} onStatusUpdate - called after a verified purchase updates the backend
 */
export async function initializeStore(onStatusUpdate) {
  if (!isIOS()) return;
  if (storeInstance) return;

  // CdvPurchase is loaded as a global via the Cordova plugin bridge
  const CdvPurchase = window.CdvPurchase;
  if (!CdvPurchase) {
    console.warn('[IAP] CdvPurchase not available');
    return;
  }

  const { store, ProductType, Platform } = CdvPurchase;
  storeInstance = store;

  // Register products
  store.register([
    {
      id: APPLE_PRODUCT_IDS.premium_monthly,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: APPLE_PRODUCT_IDS.premium_yearly,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
  ]);

  // Approved -> gate, queue the transactionId, then verify() to advance the
  // state machine. Fires at launch for every owned receipt on the device's
  // Apple ID and for the virtual `appstore.application` transaction, so only
  // user-initiated, mapped, unexpired transactions are queued for the backend.
  store.when().approved((transaction) => {
    if (!transaction.transactionId) {
      transaction.finish();
      return;
    }
    const productId = transaction.products?.[0]?.id;
    const mapping = APPLE_TO_INTERNAL[productId];
    if (Date.now() - userInitiatedAt >= USER_INITIATED_WINDOW_MS) {
      transaction.finish();
      return;
    }
    // Best-effort only: the Apple adapter rarely populates expirationDate
    // client-side — the backend verify-apple enforces expiry.
    const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
    const isExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
    if (!mapping || isExpired) {
      transaction.finish();
      return;
    }
    pendingVerify.set(transaction.transactionId, productId);
    transaction.verify();
  });

  // Verified -> drain the queue to our backend, then finish.
  // `receipt` is a VerifiedReceipt: local transactions live on
  // receipt.sourceReceipt.transactions. Send exactly what `approved` queued
  // (already window-gated) and only ids this receipt carries; finish the
  // receipt regardless of the backend outcome (silent fetch, no retry loop).
  store.when().verified((receipt) => {
    const localIds = new Set(
      (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean)
    );
    const writes = [];
    for (const [transactionId, productId] of pendingVerify) {
      if (localIds.size && !localIds.has(transactionId)) continue; // another receipt
      pendingVerify.delete(transactionId);
      writes.push(postVerifyApple({ transactionId, productId }));
    }
    receipt.finish();
    if (writes.length) {
      // Refetch AFTER the backend row exists — refreshing before the write
      // lands races it and the status query snaps back to Free.
      Promise.allSettled(writes).then(() => {
        if (onStatusUpdate) onStatusUpdate();
      });
    }
  });

  // Error handler
  store.error((error) => {
    console.error('[IAP] Store error:', error.code, error.message);
  });

  // Initialize the store
  try {
    await store.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);
    storeReady = true;
    readyCallbacks.forEach((cb) => cb());
    readyCallbacks = [];
  } catch (err) {
    console.error('[IAP] Store initialization failed:', err);
  }
}

/**
 * Get a product from the store.
 * Returns the CdvPurchase product with pricing info, or null.
 */
export function getProduct(planId) {
  if (!storeInstance || !storeReady) return null;
  const appleId = APPLE_PRODUCT_IDS[planId];
  if (!appleId) return null;
  return storeInstance.get(appleId);
}

/**
 * Get the formatted price string for a plan from the Apple store.
 * Returns null if store is not ready or product not found.
 */
export function getApplePrice(planId) {
  const product = getProduct(planId);
  if (!product) return null;
  const offer = product.getOffer();
  return offer?.pricingPhases?.[0]?.price || product.pricing?.price || null;
}

/**
 * Wait for the store to be ready.
 */
export function onStoreReady(callback) {
  if (storeReady) {
    callback();
  } else {
    readyCallbacks.push(callback);
  }
}

/**
 * Actively poll for a specific product until StoreKit has loaded it.
 *
 * Canon (shared-docs/in-app-purchases.md): wait for the REQUESTED PRODUCT, not
 * for a flat `store.ready()` timeout — on a slow sandbox network `initialize()`
 * can resolve with no products loaded, which used to surface as a dead
 * "still loading" purchase button in front of the reviewer.
 */
export async function waitForProduct(planId, timeoutMs = 15000) {
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const product = getProduct(planId);
    if (product && product.getOffer()) return product;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, 400));
  }
}

/**
 * Purchase a product via CdvPurchase.
 * MUST check return value — store.order() does NOT throw on failure.
 */
export async function purchaseProduct(planId) {
  // Poll for the product rather than rejecting on a not-yet-ready store — the
  // tap is the user-initiated moment, and StoreKit often finishes loading a
  // second or two after `initialize()` resolves.
  const product = await waitForProduct(planId);
  if (!product) {
    throw new Error('The App Store is not responding. Please try again in a moment.');
  }

  const offer = product.getOffer();
  if (!offer) {
    throw new Error('No offer available for this product');
  }

  // Open the user-initiated window so the resulting `approved` is queued.
  userInitiatedAt = Date.now();

  // MUST check return value — store.order() does NOT throw on failure
  const result = await storeInstance.order(offer);
  if (result && result.isError) {
    // User cancelled the payment sheet — not a real error, handle silently.
    // The canonical set (shared-docs/in-app-purchases.md): 6777006 =
    // PAYMENT_CANCELLED (Apple's real SKErrorPaymentCancelled mapping),
    // 6777003 = PURCHASE (generic, legacy iPadOS sandbox), 6777010 = UNKNOWN /
    // Ask-to-Buy deferral. This previously matched only 6777001, which is
    // SETUP — not a cancel at all — so a dismissed payment sheet fell through
    // to a red "Purchase failed" toast (the 2.1(b) pattern that got BabyTrack
    // build 31 rejected).
    const msg = (result.message || '').toLowerCase();
    const code = result.code;
    if (CANCEL_CODES.has(code) || msg.includes('cancel') || msg.includes('user cancelled')) {
      console.log('[IAP] User cancelled purchase');
      return; // Silent return — no error toast
    }
    throw new Error(result.message || 'Purchase failed. Please try again.');
  }
}

/**
 * Restore previous purchases (e.g. after reinstall or new device).
 */
export async function restorePurchases() {
  if (!storeInstance || !storeReady) {
    throw new Error('Store not initialized');
  }
  // Open the user-initiated window so re-delivered `approved` events are queued.
  userInitiatedAt = Date.now();
  // Same contract as store.order(): a StoreKit restore failure RESOLVES with
  // an IError rather than throwing, so surface it for the caller's toast.
  const result = await storeInstance.restorePurchases();
  // Cancels, and REFRESH_RECEIPTS (6777011 — the adapter refreshes the receipt
  // AFTER the restored transactions have already been delivered, so a failure
  // there does not mean the restore failed), must NEVER surface as an error:
  // a red toast on a purchase control is the Guideline 2.1(b) pattern.
  if (result && result.isError
      && ![6777006, 6777003, 6777010, 6777011].includes(result.code)
      && !/cancel/i.test(result.message || '')) {
    const err = new Error(result.message || 'Restore failed');
    err.code = result.code;
    throw err;
  }
  return result;
}
